

import { motion } from "motion/react";
import { Users, Calculator, DollarSign, TrendingDown, Percent } from "lucide-react";
import { useState } from "react";

export const ROICalculator = () => {
  const [hires, setHires] = useState(10);
  const [ctc, setCtc] = useState(50000);
  const [currentCost, setCurrentCost] = useState(15);
  const [isPercent, setIsPercent] = useState(true);
  const [currency, setCurrency] = useState("USD");

  const currencies = [
    { code: "USD", symbol: "$" },
    { code: "INR", symbol: "₹" },
    { code: "CAD", symbol: "C$" },
    { code: "THB", symbol: "฿" }
  ];

  const currentSymbol = currencies.find(c => c.code === currency)?.symbol || "$";

  const currentTotalCost = isPercent 
    ? (hires * ctc * (currentCost / 100))
    : (hires * currentCost);

  const plan1Cost = (hires * ctc * 0.025) + 1000;
  const plan2Cost = (hires * ctc * 0.08) + 1000;
  const savings = currentTotalCost - plan1Cost;

  const formatCurrency = (val: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      maximumFractionDigits: 0
    }).format(val);
  };

  return (
    <section className="py-32 bg-white relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-6xl font-display font-bold tracking-tight mb-6">Calculate Your Hiring ROI</h2>
            <p className="text-xl text-brand-muted font-bold italic opacity-60">Compare your current hiring costs with QuantumHire’s outcome-based pricing.</p>
          </motion.div>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="p-10 bg-brand-secondary/30 rounded-[3rem] border border-gray-100"
          >
            <div className="space-y-8">
              <div>
                <label className="block text-sm font-black text-brand-primary uppercase tracking-widest mb-4">Number of hires per year</label>
                <div className="relative">
                  <span className="absolute left-6 top-1/2 -translate-y-1/2 text-brand-muted"><Users className="w-5 h-5" /></span>
                  <input 
                    type="number" 
                    value={hires}
                    onChange={(e) => setHires(Number(e.target.value))}
                    className="w-full pl-16 pr-6 py-5 bg-white border border-gray-200 rounded-2xl font-bold text-lg focus:ring-2 focus:ring-brand-accent focus:border-transparent outline-none transition-all shadow-sm"
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-black text-brand-primary uppercase tracking-widest mb-4">Average CTC per hire</label>
                  <div className="relative">
                    <select 
                      value={currency}
                      onChange={(e) => setCurrency(e.target.value)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 bg-transparent font-black text-xs text-brand-accent outline-none cursor-pointer"
                    >
                      {currencies.map(c => <option key={c.code} value={c.code}>{c.code}</option>)}
                    </select>
                    <span className="absolute left-6 top-1/2 -translate-y-1/2 text-brand-muted font-bold">{currentSymbol}</span>
                    <input 
                      type="number" 
                      value={ctc}
                      onChange={(e) => setCtc(Number(e.target.value))}
                      className="w-full pl-12 pr-12 py-5 bg-white border border-gray-200 rounded-2xl font-bold text-lg focus:ring-2 focus:ring-brand-accent focus:border-transparent outline-none transition-all shadow-sm"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-black text-brand-primary uppercase tracking-widest mb-4">Current hiring cost</label>
                  <div className="relative">
                    <div className="absolute right-4 top-1/2 -translate-y-1/2 flex items-center bg-gray-100 p-1 rounded-lg">
                      <button 
                        onClick={() => setIsPercent(true)}
                        className={`px-3 py-1 text-[10px] font-black rounded-md transition-all ${isPercent ? 'bg-brand-primary text-white shadow-md' : 'text-gray-400'}`}
                      >
                        %
                      </button>
                      <button 
                        onClick={() => setIsPercent(false)}
                        className={`px-3 py-1 text-[10px] font-black rounded-md transition-all ${!isPercent ? 'bg-brand-primary text-white shadow-md' : 'text-gray-400'}`}
                      >
                        FLAT
                      </button>
                    </div>
                    <span className="absolute left-6 top-1/2 -translate-y-1/2 text-brand-muted font-bold">
                       {isPercent ? <Percent className="w-4 h-4" /> : currentSymbol}
                    </span>
                    <input 
                      type="number" 
                      value={currentCost}
                      onChange={(e) => setCurrentCost(Number(e.target.value))}
                      className="w-full pl-12 pr-28 py-5 bg-white border border-gray-200 rounded-2xl font-bold text-lg focus:ring-2 focus:ring-brand-accent focus:border-transparent outline-none transition-all shadow-sm"
                    />
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="p-10 bg-brand-primary rounded-[3rem] text-white shadow-2xl relative overflow-hidden"
          >
            <div className="relative z-10">
              <div className="flex items-center gap-4 mb-10">
                 <div className="w-12 h-12 rounded-2xl bg-white/10 flex items-center justify-center text-brand-accent"><Calculator className="w-6 h-6" /></div>
                 <h3 className="text-2xl font-display font-bold">Projected Savings</h3>
              </div>

              <div className="space-y-6 mb-12">
                 <div className="flex justify-between items-center py-4 border-b border-white/10">
                    <span className="text-slate-400 font-bold">Current Hiring Cost</span>
                    <span className="text-xl font-bold font-mono">{formatCurrency(currentTotalCost)}</span>
                 </div>
                 <div className="flex justify-between items-center py-4 border-b border-white/10">
                    <span className="text-slate-400 font-bold italic">QuantumHire Plan 01</span>
                    <span className="text-xl font-bold font-mono text-brand-accent">{formatCurrency(plan1Cost)}</span>
                 </div>
                 <div className="flex justify-between items-center py-4 border-b border-white/10">
                    <span className="text-slate-400 font-bold italic">QuantumHire Plan 02</span>
                    <span className="text-xl font-bold font-mono text-brand-accent">{formatCurrency(plan2Cost)}</span>
                 </div>
              </div>

              <div className="bg-brand-accent/10 border border-brand-accent/20 p-8 rounded-3xl text-center mb-8">
                 <div className="text-sm font-black text-brand-accent uppercase tracking-[0.2em] mb-2">Annual Savings Potential</div>
                 <motion.div 
                   key={savings}
                   initial={{ scale: 0.8, opacity: 0 }}
                   animate={{ scale: 1, opacity: 1 }}
                   className="text-5xl md:text-6xl font-display font-bold text-brand-accent italic mb-2"
                 >
                   {formatCurrency(savings)}
                 </motion.div>
                 <div className="text-[10px] font-black text-white/40 uppercase tracking-widest">Based on AI Matched Plan</div>
              </div>

              <p className="text-brand-secondary/60 text-center font-bold italic">"Pay only when you hire. No upfront risk."</p>
            </div>
            
            <div className="absolute top-[-10%] right-[-10%] opacity-5"><DollarSign className="w-64 h-64" /></div>
            <div className="absolute bottom-[-10%] left-[-10%] opacity-5"><TrendingDown className="w-64 h-64" /></div>
          </motion.div>
        </div>

      </div>
    </section>
  );
};
